import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Label } from '../ui/label'
import { fetchActiveProducts } from '../../services/discountService'

// Checkbox list of the store's ACTIVE products for the New Code form. Nothing
// checked means the code applies to every product (the original behavior), so
// a failed load degrades to that instead of blocking code creation.
export default function DiscountProductPicker({ selected, onChange }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetchActiveProducts()
      .then((data) => {
        if (!cancelled) setProducts(data?.products || [])
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || 'Failed to load products')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const toggle = (id, checked) => {
    onChange(checked ? [...selected, id] : selected.filter((s) => s !== id))
  }

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Label className="block">Applies to</Label>
        {selected.length > 0 && (
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => onChange([])}
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading products…
        </div>
      ) : loadError ? (
        <p className="text-xs text-destructive">
          Couldn&apos;t load products ({loadError}). The code will apply to all products.
        </p>
      ) : products.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No active products found. The code will apply to all products.
        </p>
      ) : (
        <div
          role="group"
          aria-label="Products this code applies to"
          className="max-h-48 overflow-y-auto rounded-md border border-input p-2 space-y-1.5"
        >
          {products.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(p.id)}
                onChange={(e) => toggle(p.id, e.target.checked)}
                className="h-4 w-4 shrink-0 rounded border-input"
              />
              <span className="min-w-0 break-words">{p.title}</span>
            </label>
          ))}
        </div>
      )}

      {!loading && !loadError && products.length > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          {selected.length === 0
            ? 'None checked = all products'
            : `${selected.length} product${selected.length === 1 ? '' : 's'} selected`}
        </p>
      )}
    </div>
  )
}
